import { type Practitioner } from '@/models/practitioner.ts';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';

import { ProfilePicturePlaceholder } from '@/components/Common/profile-picture-placeholder.tsx';

export type ClickablePractitioner = Practitioner & { onClick?: () => void };

interface Props {
    practitioners: ClickablePractitioner[];
}

const MAX_DESKTOP = 3;
const MAX_MOBILE = 1;

const useStyles = makeStyles((theme: any) => ({
    stack: {
        display: 'flex',
    },
    slot: {
        height: 70,
        width: 70,
        borderRadius: '50%',
        border: `2px solid ${theme.palette.common.white}`,
        marginLeft: '-0.75rem',
        boxSizing: 'border-box',
        '&:first-child': {
            marginLeft: 0,
        },
    },
    avatar: {
        objectFit: 'cover',
    },
    button: {
        cursor: 'pointer',
        backgroundColor: theme.palette.background.paper,
        padding: 0,
        overflow: 'hidden',
        appearance: 'none',
        '-webkit-appearance': 'none',
        '&:focus-visible': {
            outline: `2px solid ${theme.palette.common.darkGrey}`,
            outlineOffset: 2,
        },
    },
    innerImg: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
        borderRadius: '50%',
    },
    moreCircle: {
        backgroundColor: theme.palette.common.cream,
        color: theme.palette.common.darkGrey,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: theme.weight.heading,
        fontSize: '1rem',
        letterSpacing: '0.1em',
    },
}));

export const PractitionerPictureStack = ({ practitioners }: Props) => {
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
    const max = isMobile ? MAX_MOBILE : MAX_DESKTOP;
    const visible = practitioners.slice(0, max);
    const remaining = practitioners.length - visible.length;

    return (
        <div className={classes.stack}>
            {visible.map((p) => {
                const hasPhoto = Boolean(p.url);

                if (p.onClick) {
                    return (
                        <button
                            key={p.key}
                            type="button"
                            onClick={p.onClick}
                            className={clsx(classes.slot, classes.button)}
                            aria-label={p.name}
                        >
                            {hasPhoto ? (
                                <img src={p.url} alt="" className={classes.innerImg} />
                            ) : (
                                <ProfilePicturePlaceholder className={classes.innerImg} />
                            )}
                        </button>
                    );
                }

                return hasPhoto ? (
                    <img key={p.key} src={p.url} alt={p.name} className={clsx(classes.slot, classes.avatar)} />
                ) : (
                    <ProfilePicturePlaceholder
                        key={p.key}
                        className={clsx(classes.slot, classes.avatar)}
                        label={p.name}
                    />
                );
            })}
            {remaining > 0 && (
                <div
                    className={clsx(classes.slot, classes.moreCircle)}
                    role="img"
                    aria-label={`and ${remaining} more practitioner${remaining > 1 ? 's' : ''}`}
                >
                    <span aria-hidden="true">···</span>
                </div>
            )}
        </div>
    );
};
